import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { focusShared } from "../lib/deepLink";
import { PageHeader } from "../components/PageHeader";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CloudRain, Map as MapIcon, TriangleAlert, Database } from "lucide-react";

const statusBadge = {
  Penanganan: "bg-red-100 text-red-700",
  Selesai: "bg-emerald-100 text-emerald-700",
  Pemantauan: "bg-amber-100 text-amber-700",
};

export default function InfoKebencanaan() {
  const [disasters, setDisasters] = useState([]);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [maps, setMaps] = useState([]);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get("/disasters").then((r) => setDisasters(r.data)).catch(() => {});
    api.get("/disasters/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/maps").then((r) => setMaps(r.data)).catch(() => {});
    const bencana = searchParams.get("bencana");
    const peta = searchParams.get("peta");
    const peringatan = searchParams.get("peringatan");
    if (bencana) focusShared(`bencana-${bencana}`);
    else if (peta) focusShared(`peta-${peta}`);
    else if (peringatan) focusShared(`peringatan-${peringatan}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const yearData = stats ? Object.entries(stats.by_year).sort().map(([year, count]) => ({ year, kejadian: count })) : [];

  return (
    <div data-testid="info-kebencanaan-page">
      <PageHeader title="Informasi Kebencanaan" subtitle="Data kejadian, peta wilayah rawan, dan peringatan dini bencana Kabupaten Kayong Utara" />

      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center gap-3 mb-8">
          <TriangleAlert className="text-orange-600" size={22} />
          <h2 className="font-heading text-lg font-bold text-slate-900">Peringatan Dini Aktif</h2>
        </div>
        {alerts.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded p-8 text-center text-slate-400" data-testid="alerts-empty">
            Tidak ada peringatan dini aktif saat ini. Sumber data: BMKG, BNPB & instansi terkait.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {alerts.map((a) => (
              <div key={a.id} id={`peringatan-${a.id}`} className="border-l-4 border-orange-600 bg-white border border-slate-200 rounded p-5" data-testid={`alert-card-${a.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-[11px] uppercase tracking-wider bg-orange-100 text-orange-700 px-2 py-0.5 rounded">{a.level}</span>
                  <span className="font-mono text-xs text-slate-400">{a.source}</span>
                </div>
                <h3 className="font-heading font-semibold text-slate-900">{a.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5">{a.message}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-slate-100 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center gap-3 mb-8">
            <Database className="text-orange-600" size={22} />
            <h2 className="font-heading text-lg font-bold text-slate-900">Rekapitulasi Bencana per Tahun</h2>
          </div>
          {yearData.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded p-8 text-center text-slate-400 bg-white" data-testid="chart-empty">
              Belum ada data kejadian bencana yang tercatat.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded p-6 h-72" data-testid="disaster-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={yearData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" fontSize={12} fontFamily="JetBrains Mono" />
                  <YAxis fontSize={12} fontFamily="JetBrains Mono" allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="kejadian" fill="#EA580C" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-8">Laporan Kejadian Bencana Terkini</h2>
        <div className="border border-slate-200 rounded overflow-x-auto bg-white" data-testid="disasters-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-950 text-white">
              <tr>
                <th className="text-left px-4 py-3 font-heading">Tanggal</th>
                <th className="text-left px-4 py-3 font-heading">Jenis Bencana</th>
                <th className="text-left px-4 py-3 font-heading">Lokasi</th>
                <th className="text-left px-4 py-3 font-heading">Terdampak</th>
                <th className="text-left px-4 py-3 font-heading">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {disasters.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Belum ada data kejadian bencana.</td></tr>
              )}
              {disasters.map((d) => (
                <tr key={d.id} id={`bencana-${d.id}`} className="hover:bg-orange-50/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs">{d.date}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{d.disaster_type}</td>
                  <td className="px-4 py-3 text-slate-600">{d.location}</td>
                  <td className="px-4 py-3 font-mono">{d.affected ?? 0} jiwa</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge[d.status] || "bg-slate-100 text-slate-600"}`}>{d.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center gap-3 mb-8">
            <MapIcon className="text-orange-500" size={22} />
            <h2 className="font-heading text-lg font-bold">Peta & Informasi Wilayah</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <iframe
                title="Peta Wilayah Kayong Utara"
                src="https://www.openstreetmap.org/export/embed.html?bbox=109.5%2C-1.9%2C110.7%2C-0.7&layer=mapnik&marker=-1.2454%2C110.0743"
                className="w-full h-96 rounded border border-slate-800"
                data-testid="region-map"
              />
              <p className="text-xs text-slate-500 mt-2">Peta wilayah Kabupaten Kayong Utara. Peta rawan bencana detail tersedia di kantor BPBD.</p>
            </div>
            <div className="space-y-4">
              {[
                { title: "Peta Rawan Bencana", desc: "Zona rawan banjir, longsor, karhutla, dan abrasi pantai per kecamatan." },
                { title: "Jalur Evakuasi", desc: "Rute evakuasi resmi menuju titik kumpul aman di tiap kecamatan." },
                { title: "Titik Kumpul & Posko", desc: "Lokasi posko darurat dan titik kumpul yang telah ditetapkan." },
              ].map((m) => (
                <div key={m.title} className="border border-slate-800 rounded p-5">
                  <h3 className="font-heading font-semibold text-orange-400">{m.title}</h3>
                  <p className="text-sm text-slate-400 mt-1.5">{m.desc}</p>
                </div>
              ))}
              <div className="border border-slate-800 rounded p-5 flex gap-3 items-start">
                <CloudRain className="text-orange-500 shrink-0" size={20} />
                <p className="text-sm text-slate-400">
                  Informasi cuaca ekstrem bersumber dari <a href="https://bmkg.go.id" target="_blank" rel="noreferrer" className="text-orange-400 underline">BMKG</a> dan <a href="https://bnpb.go.id" target="_blank" rel="noreferrer" className="text-orange-400 underline">BNPB</a>.
                </p>
              </div>
            </div>
          </div>
          {maps.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading font-semibold text-orange-400 mb-4">Peta yang Dipublikasikan</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="published-maps">
                {maps.map((m) => (
                  <a key={m.id} id={`peta-${m.id}`} href={m.image_url} target="_blank" rel="noreferrer" className="group border border-slate-800 rounded overflow-hidden hover:border-orange-500 transition-colors block" data-testid={`map-item-${m.id}`}>
                    <img src={m.image_url} alt={m.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="p-4">
                      <span className="font-mono text-[11px] uppercase tracking-wider text-orange-500">{m.category}</span>
                      <p className="font-heading font-semibold mt-1">{m.title}</p>
                      {m.description && <p className="text-xs text-slate-400 mt-1">{m.description}</p>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
